/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {PartNumberChangesFooterActionModel} from '../models/part-number-changes-footer-action.model';
import {PartNumberChangesDetailFooterActionsEnum} from '../models/part-number-changes-detail-footer-actions.enum';
import {PartNumberChangeTrialCommandModel} from '../../api/ms-partnumberchanges/contracts/part-number-change-trial-command.model';
import {StatefulComponent} from '../../shared/stateful/stateful.component';
import {PartNumberChangesDetailStateModel} from './part-number-changes-detail-state.model';
import {PartNumberChangeSolveCommandModel} from '../../api/ms-partnumberchanges/contracts/part-number-change-solve-command.model';
import {PartNumberChangesDetailSolutionState} from '../part-number-changes-solution/part-number-changes-detail-solution.state';
import {Subscription} from 'rxjs';
import {PartNumberChangesDetailComponentsModel} from '../services/part-number-changes-detail-configuration/models/part-number-changes-detail-components.model';
import {PartNumberChangesDisplayConfigurationService} from '../services/part-number-changes-detail-configuration/part-number-changes-display-configuration.service';
import {PartNumberChangesPageTypeEnum} from '../services/part-number-changes-detail-configuration/models/part-number-changes-page-type.enum';
import {PartNumberChangesCreateComponentsService} from '../services/part-number-changes-create-components/part-number-changes-create-components.service';
import {PartNumberChangesDisplayComponentsTypeEnum} from '../services/part-number-changes-detail-configuration/models/part-number-changes-display-components-type.enum';
import {PartNumberChangesOldCredentialsFormState} from '../models/part-number-changes-old-credentials-form.state';
import {RouteSearchCriteriaModel} from '../../api/ms-partnumberchanges/contracts/route-search-criteria.model';
import {PncDetailModel} from '../models/pnc-detail/pnc-detail.model';
import {InputDisplayModeEnum} from '../../shared/input/input-display-mode.enum';
import {PartNumberChangesStickyHeaderActionModel} from '../models/part-number-changes-sticky-header-action.model';
import {PncSolutionUtilService} from '../services/pnc-solution-util/pnc-solution-util.service';
import {PartNumberChangesAiSolutionState} from '../part-number-changes-ai-solution/part-number-changes-ai-solution.state';
import PNC_DISPLAY_CONFIGURATION_CONSTANTS
    from '../services/part-number-changes-detail-configuration/part-number-changes-display-constants';
import {PartNumberChangesDetailFooterComponent} from '../part-number-changes-footer/part-number-changes-detail-footer.component';
import {ButtonDirectionEnum} from '../services/part-number-changes-create-components/button-direction.enum';
import {toBase64Utf16LittleEndian} from '../../shared/utils/encode/encodeToBase64Utf16LE';

@Component({
    selector: 'app-part-number-changes-detail',
    templateUrl: './part-number-changes-detail.component.html',
    styleUrls: ['../styles/pnc-base.component.css', './part-number-changes-detail.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PartNumberChangesDetailComponent
        extends StatefulComponent<PartNumberChangesDetailStateModel>
        implements OnInit, OnDestroy, AfterViewInit {

    @ViewChild('stickyHeaderContainer', {read: ViewContainerRef}) stickyHeaderContainer: ViewContainerRef;
    @ViewChild('headerContainer', {read: ViewContainerRef}) headerContainer: ViewContainerRef;
    @ViewChild('bodyContainer', {read: ViewContainerRef}) bodyContainer: ViewContainerRef;
    @ViewChild('footerContainer', {read: ViewContainerRef}) footerContainer: ViewContainerRef;

    @Input() detailData: PncDetailModel = <PncDetailModel>{};
    @Input() updateState: EventEmitter<Partial<PartNumberChangesDetailStateModel>> =
            new EventEmitter<Partial<PartNumberChangesDetailStateModel>>();
    @Output() footerAction: EventEmitter<PartNumberChangesFooterActionModel> = new EventEmitter<PartNumberChangesFooterActionModel>();
    @Output() dialogHistoryAction: EventEmitter<void> = new EventEmitter<void>();
    @Output() stickyHeaderAction: EventEmitter<PartNumberChangesStickyHeaderActionModel> =
            new EventEmitter<PartNumberChangesStickyHeaderActionModel>();

    public newTrialData: PartNumberChangeTrialCommandModel;

    private componentsConfiguration: PartNumberChangesDetailComponentsModel[];
    private solutionData: PartNumberChangeSolveCommandModel;
    private subscriptions: Subscription = new Subscription();
    private readonly buttonLabelRight = 'Solution dialog history';

    private get routeSearchCriteria(): RouteSearchCriteriaModel {
        return {
            partnerCode: this.detailData?.partner?.code,
            shipToPartyCode: this.detailData?.shipToParty?.code,
            supplyingPackingPlantCode: this.detailData?.supplyingPackingPlant?.code
        };
    }

    constructor(private detailConfiguration: PartNumberChangesDisplayConfigurationService,
                private cdr: ChangeDetectorRef,
                private componentsService: PartNumberChangesCreateComponentsService,
                private solutionUtil: PncSolutionUtilService) {
        super();
    }

    ngOnInit(): void {
        this.storeState({
            isSolving: !!this.detailData?.requiresFlashing,
            showAllTrials: false
        });

        this.subscriptions.add(
                this.updateState.subscribe((state: Partial<PartNumberChangesDetailStateModel>) => {
                    this.storeState(state);
                    this.updateDisplayConfiguration();
                }));
    }

    ngAfterViewInit(): void {
        this.updateDisplayConfiguration();
    }

    ngOnDestroy(): void {
        this.componentsService.destroyComponents();
        this.subscriptions.unsubscribe();
    }

    get getDialogHistoryStatus(): boolean {
        return this.state.showAllTrials;
    }

    dialogHistoryButtonDirection(title: string): ButtonDirectionEnum {
        if (title === this.buttonLabelRight) {
            return ButtonDirectionEnum.RIGHT;
        } else {
            return ButtonDirectionEnum.LEFT;
        }
    };

    private updateDisplayConfiguration(timer = false): void {
        this.componentsConfiguration = this.detailConfiguration
                .retrievePNCDisplayConfiguration(PartNumberChangesPageTypeEnum.DETAIL, this.detailData, this.state);
        // TODO: CTWILS-6002 - Chrome forces scroll up when destroying and creating components right after.
        //  Timer queues a new task on the event loop to be addressed later.
        //  The timer's dueTime gives enough time for the browser to render the components destruction, and then subsequent construction.
        //  We can update components with timer by passing the timer = true.
        if (timer) {
            this.componentsService.updateComponentsWithTimer(this.createComponents, this.componentsConfiguration);
        } else {
            this.componentsService.updateComponents(this.createComponents, this.componentsConfiguration);
        }
    }

    private dialogHistoryClick = (): void => {
        this.storeState({
            showAllTrials: !this.state.showAllTrials
        });
        this.componentsService.updateTrialsComponents(this.state.showAllTrials, this.detailData);
    };

    private footerClick = (action: PartNumberChangesDetailFooterActionsEnum): void => {
        if (action !== PartNumberChangesDetailFooterActionsEnum.SOLVE
                && action !== PartNumberChangesDetailFooterActionsEnum.SEND_SOLUTION
                && action !== PartNumberChangesDetailFooterActionsEnum.DISCARD_SOLVE) {
            this.updateStateBasedOnAction(action);

            if (action !== PartNumberChangesDetailFooterActionsEnum.SEND_TRIAL
                    && action !== PartNumberChangesDetailFooterActionsEnum.REJECT_SOLUTION
                    && action !== PartNumberChangesDetailFooterActionsEnum.DISCARD_SOLVE_AFTER_EE) {
                this.updateDisplayConfiguration(true);
            }
        }

        this.footerAction.emit({
            type: action,
            pcnData: this.detailData,
            newPncTrial: this.newTrialData,
            solutionData: this.solutionData
        });
    };

    private doneClick = (action: boolean): void => {
        this.stickyHeaderAction.emit({done: action, pncDetail: this.detailData});
    };

    private mapPNCDetailChangeTrial = (item: PartNumberChangesOldCredentialsFormState): void => {
        this.newTrialData = {
            salesVariant: item?.salesVariant,
            modelCode: item.modelCode,
            colourCode: item?.colourCode,
            fullLotNumber: item.fullLotNumber,
            problemDescription: toBase64Utf16LittleEndian(item?.problemDescription),
            additionalFullLotNumbers: item?.additionalFullLotNumbers,
            spoNumber: item?.spoNumber?.spoNumber,
            additionalSpoNumbers: item?.additionalSpoNumbers?.map((spoNumber) => spoNumber.spoNumber)
        };
    };

    private setTrialIsValid = (isValid: boolean): void => {
        this.storeState({trialIsValid: isValid});
        this.setButtonActions();
    };

    private trialTitleSolution = (titleTrial: string): void => {
        this.storeState({titleTrial: titleTrial});
    };

    private setButtonActions(): void {
        this.updateFooter(this.state?.isRequiresFlashingToggleActive, this.state?.showEESolution);
        const footer = this.componentsConfiguration.find((component) =>
                component.type === PartNumberChangesDisplayComponentsTypeEnum.FOOTER);

        const displayModeSolution = this.componentsConfiguration.find((component) =>
                component.type === PartNumberChangesDisplayComponentsTypeEnum.SOLUTION)
                ?.data?.displayConfiguration?.quantityInput?.displayMode;

        const displayModeCurrentTrial = this.componentsConfiguration.find((component) =>
                component.type === PartNumberChangesDisplayComponentsTypeEnum.CURRENT_TRIAL)
                ?.data?.displayConfiguration?.modelCodeInput?.displayMode;

        if (footer != null && footer.data?.primaryButton != null) {
            footer.data.primaryButton.disable = false;
            if (displayModeSolution === InputDisplayModeEnum.EDIT) {
                footer.data.primaryButton.disable = !this.state.solutionIsValid;
                const footerComponent = this.getFooterComponent();
                if (footerComponent != null) {
                    footerComponent.instance.configuration.primaryButton.disable = !this.state.solutionIsValid;
                }
            }
            if (displayModeCurrentTrial === InputDisplayModeEnum.EDIT) {
                footer.data.primaryButton.disable = !this.state.trialIsValid;
            }

            if (this.state.showEESolution) {
                const footerComponent = this.getFooterComponent();
                footerComponent.instance.configuration.primaryButton.disable = !this.state.isEESolutionValid;
            }
        }
        this.storeState({isRequiresFlashingToggleActive: null});
        this.cdr.detectChanges();
    };

    private getFooterComponent(): ComponentRef<any> {
        return this.componentsService.createdComponentsData
                .find((components) => components.componentType === PartNumberChangesDetailFooterComponent);
    }

    private mapPNCSolution = (item: PartNumberChangesDetailSolutionState): void => {
        this.solutionData = {
            material: this.solutionUtil.convertSolutionMaterial(item.material),
            additionalInformation: toBase64Utf16LittleEndian(item.additionalInformation) ?? null,
            additionalMaterials: item.additionalMaterials?.map((additional) =>
                    this.solutionUtil.convertSolutionMaterial(additional)) ?? null,
            eeSolution: {
                iLevel: item.eeSolution?.iLevel,
                eeAdditionalInformation: toBase64Utf16LittleEndian(item.eeSolution?.additionalInformationSolution)
            },
            solutionSupplyingPackingPlant: item.solutionSupplyingPackingPlant
        };
        this.storeState({
            solutionIsValid: item.isValid,
            isEESolutionValid: item.eeSolution?.isValid,
            isRequiresFlashingToggleActive: item?.isRequiresFlashingToggleActive
        });
        this.setButtonActions();
    };

    private updateFooter = (isRequiresFlashingToggleActive: boolean, showEESolution: boolean): void => {
        if (isRequiresFlashingToggleActive != null) {
            const value = isRequiresFlashingToggleActive ? PNC_DISPLAY_CONFIGURATION_CONSTANTS.footer.pncSendToEESpecialist
                    : PNC_DISPLAY_CONFIGURATION_CONSTANTS.footer.technicianSolve;
            this.componentsService.updateFooterSection(value);
        } else if (showEESolution) {
            const value = PNC_DISPLAY_CONFIGURATION_CONSTANTS.footer.pncSendToTechnician;
            this.componentsService.updateFooterSection(value);
        }
    };

    private mapAiSolution = (item: PartNumberChangesAiSolutionState): void => {
        this.solutionData = {
            material: this.solutionUtil.convertSolutionMaterial(item),
            additionalInformation: toBase64Utf16LittleEndian(item.additionalInformation) ?? null,
            solutionSupplyingPackingPlant: null
        };
        this.storeState({solutionIsValid: item.isValid});
        this.setButtonActions();
    };

    private updateStateBasedOnAction(action: PartNumberChangesDetailFooterActionsEnum): void {
        const state: Partial<PartNumberChangesDetailStateModel> = {
            isSolving: action === PartNumberChangesDetailFooterActionsEnum.ACCEPT,
            showEESolution: action === PartNumberChangesDetailFooterActionsEnum.ACCEPT_SOLUTION
        };
        this.storeState(state);
    }

    private createComponents = (components: PartNumberChangesDetailComponentsModel[]): void => {
        components.forEach((item) => {
            switch (item.type) {
                case PartNumberChangesDisplayComponentsTypeEnum.STICKY_HEADER:
                    this.componentsService.mapStickyHeaderSection(item.data, this.stickyHeaderContainer, this.doneClick);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.HEADER:
                    this.componentsService.mapHeaderSection(item.data, this.headerContainer);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.SECTION_TITLE:
                    this.componentsService.mapTitleSection(item.data, this.bodyContainer);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.CREDENTIALS:
                    this.componentsService.mapCredentialsSection(item.data, this.bodyContainer);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.DIALOG_HISTORY_BUTTON:
                    this.componentsService.mapDialogHistoryButtonSection(
                            this.bodyContainer,
                            item.data,
                            this.getDialogHistoryStatus, this.dialogHistoryClick, this.dialogHistoryButtonDirection(item.data));
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.TRIALS:
                    this.componentsService.mapTrialsSection(
                            item.data,
                            this.bodyContainer,
                            this.detailData.status?.code,
                            this.state.showAllTrials);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.CURRENT_TRIAL:
                    this.componentsService.mapCurrentTrialSection(
                            item.data,
                            this.bodyContainer,
                            this.setTrialIsValid,
                            this.mapPNCDetailChangeTrial,
                            this.routeSearchCriteria,
                            this.trialTitleSolution);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.SOLUTION:
                    this.componentsService.mapSolutionSection(
                            item.data,
                            this.bodyContainer,
                            this.detailData?.supplyingPackingPlant?.code,
                            this.detailData?.solutionSupplyingPackingPlant,
                            this.detailData?.alternativeSupplyingPackingPlants,
                            this.mapPNCSolution,
                            null,
                            null,
                            this.detailData?.solutionTrials?.length,
                            this.state.showEESolution);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.SOLUTION_TRIALS:
                    this.componentsService.mapSolutionTrialsSection(
                            item.data,
                            this.bodyContainer);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.AI_SOLUTION:
                    this.componentsService.mapAiSolutionSection(
                            item.data,
                            this.bodyContainer,
                            this.detailData?.supplyingPackingPlant?.code,
                            this.mapAiSolution);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.CANCELLED:
                    this.componentsService.mapCancelledSection(
                            item.data,
                            this.bodyContainer);
                    break;
                case PartNumberChangesDisplayComponentsTypeEnum.FOOTER:
                    this.componentsService.mapFooterSection(item.data, this.footerContainer, this.footerClick);
                    break;
                default:
                    return;
            }
        });
    };
}
